import { BAND_COUNT, effectiveEnergy, isDimmed, type Band, type Motivation } from '@hmawiw/shared';
import { useEffect, useLayoutEffect, useMemo, useRef, useState, type CSSProperties, type ReactNode } from 'react';
import { useSearchParams } from 'react-router';
import { useCardGestures, type DragState } from '../../dnd/useCardGestures';
import { useSingleColumn, useNow } from '../../hooks/useMedia';
import { usePullToRefresh } from '../../hooks/usePullToRefresh';
import { useT } from '../../i18n';
import { cellCardsFor, dueForHorizon } from '../../lib/horizon';
import { haptic } from '../../native/platform';
import { boardCards, useCards, useBoardCards } from '../../store/cards';
import { useSession } from '../../store/session';
import { useSettings } from '../../store/settings';
import { useUi } from '../../store/ui';
import { useTour } from '../../tour/store';
import { CardSheet } from '../CardSheet/CardSheet';
import { CardView } from '../Card/Card';
import { Plus } from '../icons';
import { ColumnStrip } from '../ColumnBar/ColumnBar';
import { QuickAdd } from '../QuickAdd/QuickAdd';
import { Tour } from '../Tour/Tour';
import { InboxTray } from '../Inbox/InboxTray';
import { CheckinSheet } from '../Levers/CheckinSheet';
import { MomentBar } from '../Levers/MomentBar';
import { PinnedBar } from '../Levers/PinnedBar';
import { TimerBar } from '../TimerBar/TimerBar';
import { TopBar } from '../TopBar/TopBar';
import { UpdateBanner } from '../UpdateBanner/UpdateBanner';
import styles from './Board.module.css';
import { openCardAnywhere } from '../../lib/openCard';
import { BoardDock } from '../Boards/BoardDock';
import { useOrderedBoards } from '../../hooks/useBoards';

const BANDS = Array.from({ length: BAND_COUNT }, (_, i) => i as Band);

export function Board() {
  const t = useT();
  const single = useSingleColumn();
  const several = useOrderedBoards().length > 1;
  const now = useNow();
  const cards = useBoardCards();
  const moveCard = useCards((s) => s.moveCard);
  const preset = useSettings((s) => s.bandPreset);
  const columnCount = useSettings((s) => s.columnCount);
  const COLUMNS = useMemo(() => Array.from({ length: columnCount }, (_, i) => i as Motivation), [columnCount]);
  const energyNow = useSettings((s) => s.energyNow);
  const effective = effectiveEnergy(energyNow, now);
  const column = useUi((s) => s.column);
  const setColumn = useUi((s) => s.setColumn);
  const showToast = useUi((s) => s.showToast);
  const introSeen = useUi((s) => s.introSeen);
  const freshAccount = useSession((s) => s.freshAccount);
  const tourActive = useTour((s) => s.active);
  const startTour = useTour((s) => s.start);
  const boardRef = useRef<HTMLDivElement>(null);

  // A newly created account gets the tour once; a returning user on a new device does not.
  useEffect(() => {
    if (freshAccount && !introSeen && !tourActive) void startTour();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // /?card=<id> opens that card (notification taps land here); the sheet waits for the card if it is still syncing.
  // /?checkin=1 is the morning check-in's tap: energy, then one dreaded thing for the day.
  // /?add=1 opens quick add (the widget and the icon shortcut); with &inbox=1 the card is sorted later.
  const [params, setParams] = useSearchParams();
  const openQuickAddFor = useUi((s) => s.openQuickAdd);
  const [checkin, setCheckin] = useState(false);
  useEffect(() => {
    const id = params.get('card');
    if (params.get('checkin')) setCheckin(true);
    if (params.get('add')) openQuickAddFor({ inbox: params.get('inbox') === '1' });
    if (!id && !params.get('checkin') && !params.get('add')) return;
    if (id) openCardAnywhere(id);
    setParams(new URLSearchParams(), { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params]);
  const pullRef = useRef<HTMLDivElement>(null);
  const pull = usePullToRefresh(boardRef, pullRef, single);

  const drag = useCardGestures(boardRef, {
    onMove: (id, m, b, index) => {
      const c = cards[id];
      if (!c) return;
      if (preset === 'horizon') {
        // A horizon band is a due date, not a place: the drop sets the date and keeps the importance band.
        const dueAt = dueForHorizon(b, c, now);
        const among = cellCardsFor(cards, m, b, 'horizon', now);
        void moveCard(id, m, c.band, b === 2 ? index : 0, { among, changes: { dueAt }, cause: 'horizon' });
        return;
      }
      void moveCard(id, m, b, index, { cause: 'drag' });
    },
    canSwipe: (id, dir) => {
      const c = cards[id];
      return !!c && c.motivation + dir >= 0 && c.motivation + dir < columnCount;
    },
    onSwipe: (id, dir) => {
      const c = cards[id];
      if (!c) return;
      const from = c.motivation;
      const to = (from + dir) as Motivation;
      void moveCard(id, to, c.band, 0, { cause: 'swipe' });
      showToast(t.t('toast.movedTo', { column: t.column(to) }), {
        label: t.t('toast.undo'),
        run: () => {
          void moveCard(id, from, c.band, 0, { cause: 'undo' });
        },
      });
    },
  });

  // Land on the remembered column before the first paint; the scroll listener keeps the store in step afterwards.
  useLayoutEffect(() => {
    const el = boardRef.current;
    if (!el || !single) return;
    el.scrollLeft = column * el.clientWidth;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [single]);

  const anim = useRef<number | null>(null);

  const stopAnimation = () => {
    const el = boardRef.current;
    if (anim.current !== null) cancelAnimationFrame(anim.current);
    anim.current = null;
    if (el) el.style.scrollSnapType = '';
  };

  useEffect(() => {
    const el = boardRef.current;
    if (!el || !single) return;
    // The strip follows the swipe itself: the column changes the moment the board passes the
    // halfway point, not when the snap settles, so name and cards move together.
    let frame: number | null = null;
    const onScroll = () => {
      if (anim.current !== null) return; // programmatic motion sets the column itself
      if (frame !== null) return;
      frame = requestAnimationFrame(() => {
        frame = null;
        const idx = Math.round(el.scrollLeft / el.clientWidth);
        if (idx !== useUi.getState().column) {
          setColumn(idx);
          haptic('tick');
        }
      });
    };
    const onResize = () => {
      el.scrollLeft = useUi.getState().column * el.clientWidth;
    };
    const onPointerDown = () => stopAnimation();
    el.addEventListener('scroll', onScroll, { passive: true });
    el.addEventListener('pointerdown', onPointerDown, { passive: true });
    window.addEventListener('resize', onResize);
    return () => {
      el.removeEventListener('scroll', onScroll);
      el.removeEventListener('pointerdown', onPointerDown);
      window.removeEventListener('resize', onResize);
      if (frame !== null) cancelAnimationFrame(frame);
      stopAnimation();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [single, setColumn]);

  /**
   * Programmatic column change: the target is committed at once so repeated taps queue up,
   * and the board glides there in about 200 ms with snapping paused, faster than the
   * browser's own smooth scroll. `record` is false when the back button walks the history.
   */
  const goColumn = (i: number, record = true) => {
    const el = boardRef.current;
    if (!el) return;
    const target = Math.max(0, Math.min(columnCount - 1, i)) as Motivation;
    if (target !== useUi.getState().column) haptic('tick');
    setColumn(target, record);
    if (anim.current !== null) cancelAnimationFrame(anim.current);
    el.style.scrollSnapType = 'none';
    const from = el.scrollLeft;
    const to = target * el.clientWidth;
    const start = performance.now();
    const duration = 200;
    const step = (now: number) => {
      const p = Math.min(1, (now - start) / duration);
      const eased = 1 - (1 - p) ** 3;
      el.scrollLeft = from + (to - from) * eased;
      if (p < 1) {
        anim.current = requestAnimationFrame(step);
      } else {
        anim.current = null;
        el.scrollLeft = to;
        el.style.scrollSnapType = '';
      }
    };
    anim.current = requestAnimationFrame(step);
  };

  // The board slides in from the side the flick came from, so the change reads as movement.
  const boardSlide = useUi((s) => s.boardSlide);
  useEffect(() => {
    const el = boardRef.current;
    if (!boardSlide || !el || typeof el.animate !== 'function') return;
    el.animate([{ transform: `translateY(${boardSlide.dir * 28}px)`, opacity: 0.35 }, { transform: 'none', opacity: 1 }], { duration: 180, easing: 'cubic-bezier(.2,.7,.3,1)' });
  }, [boardSlide]);

  // Alt with Up or Down steps through the boards, from a keyboard on any screen.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (!e.altKey || (e.key !== 'ArrowUp' && e.key !== 'ArrowDown')) return;
      if ((e.target as HTMLElement | null)?.closest?.('input, textarea, select, [role="dialog"]')) return;
      e.preventDefault();
      const dir = e.key === 'ArrowDown' ? 1 : -1;
      if (useSession.getState().stepBoard(dir)) useUi.getState().slideBoard(dir);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  const jump = useUi((s) => s.jump);
  useEffect(() => {
    if (jump && single) goColumn(jump.column, false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [jump]);

  return (
    <div className={`${styles.page}${single ? '' : ` ${styles.wide}`}`}>
      <TopBar />
      <UpdateBanner />
      <TimerBar />
      <InboxTray />
      <PinnedBar />
      <MomentBar />
      <div className={styles.boardWrap}>
        <div ref={boardRef} className={styles.board} style={{ '--cols': columnCount } as CSSProperties}>
          {COLUMNS.map((m) => (
            <Column key={m} m={m} drag={drag} effective={effective} now={now} />
          ))}
        </div>
        {single ? (
          <div ref={pullRef} className={styles.pull} data-state={pull} aria-hidden="true">
            <svg className={styles.pullArrow} width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M12 5v14M6 13l6 6 6-6" />
            </svg>
            <svg className={styles.pullRing} width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round">
              <path d="M12 3a9 9 0 1 1-6.4 2.6" />
            </svg>
          </div>
        ) : null}
      </div>
      {single ? <ColumnStrip onGo={goColumn} /> : several ? <BoardDock /> : null}
      <CardSheet />
      <CheckinSheet open={checkin} onClose={() => setCheckin(false)} />
      <QuickAdd />
      <Tour />
    </div>
  );
}

interface ColumnProps {
  m: Motivation;
  drag: DragState | null;
  effective: ReturnType<typeof effectiveEnergy>;
  now: number;
}

function Column({ m, drag, effective, now }: ColumnProps) {
  const t = useT();
  const cards = useBoardCards();
  const count = useMemo(() => boardCards(cards, now).filter((c) => c.motivation === m).length, [cards, m, now]);
  return (
    <section className={styles.col} data-col={m} style={{ '--m': `var(--m${m})` } as CSSProperties} aria-label={t.column(m)}>
      <div className={styles.colHead} data-tour="colhead">
        <span className={`display ${styles.colName}`}>{t.column(m)}</span>
        <span className={styles.count}>{t.tn('count.cards', count)}</span>
      </div>
      {BANDS.map((b) => (
        <BandView key={b} m={m} b={b} drag={drag} effective={effective} now={now} />
      ))}
    </section>
  );
}

interface BandProps extends ColumnProps {
  b: Band;
}

function BandView({ m, b, drag, effective, now }: BandProps) {
  const t = useT();
  const cards = useBoardCards();
  const preset = useSettings((s) => s.bandPreset);
  const openQuickAdd = useUi((s) => s.openQuickAdd);
  const list = useMemo(() => cellCardsFor(cards, m, b, preset, now).filter((c) => c.id !== drag?.id), [cards, m, b, preset, now, drag?.id]);
  const placeholderAt = drag && drag.motivation === m && drag.band === b ? Math.min(drag.index, list.length) : -1;
  const items: ReactNode[] = [];
  list.forEach((card, i) => {
    if (i === placeholderAt) items.push(<Placeholder key="placeholder" height={drag?.height ?? 56} />);
    items.push(<CardView key={card.id} card={card} dimmed={isDimmed(card.energy, effective)} now={now} />);
  });
  if (placeholderAt === list.length) items.push(<Placeholder key="placeholder" height={drag?.height ?? 56} />);
  return (
    <div className={styles.band} data-cell="" data-m={m} data-b={b}>
      <div className={styles.bandLabel} data-tour={b === 0 ? 'band' : undefined}>
        <span>{t.band(b)}</span>
        {/* The band's own +: the new card starts in this column and this band. */}
        <button
          type="button"
          className={styles.bandAdd}
          data-tour={b === 0 ? 'add' : undefined}
          onClick={() => openQuickAdd({ motivation: m, band: b })}
          aria-label={t.t('add.toBand', { column: t.column(m), band: t.band(b) })}
          title={t.t('add.toBand', { column: t.column(m), band: t.band(b) })}
        >
          <Plus size={16} />
        </button>
      </div>
      {items}
    </div>
  );
}

function Placeholder({ height }: { height: number }) {
  return <div className={styles.placeholder} style={{ height }} aria-hidden="true" />;
}
